import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'

import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans, msg } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import Link from 'next/link'
import { useRouter } from 'next/router'

import styles from './index.module.css'

const PRODUCT_ITEMS = [
  {
    label: msg`Taylora Pages`,
    path: 'taylora-pages',
    icon: '/navbar-icons/document_duplicate.svg',
  },
  { label: msg`Video`, path: 'video', icon: '/navbar-icons/play.svg' },
  { label: msg`Ebook`, path: 'ebook', icon: '/navbar-icons/book_open.svg' },
  { label: msg`Link`, path: 'links', icon: '/navbar-icons/link.svg' },
  {
    label: msg`Corsi`,
    path: 'courses',
    icon: '/navbar-icons/academic_cap.svg',
  },
  {
    label: msg`Condivisione Privata`,
    path: 'private-sharing',
    icon: '/navbar-icons/lock_closed.svg',
  },
  {
    label: msg`Vendita Online`,
    path: 'online-sale',
    icon: '/navbar-icons/currency_dollar.svg',
  },
]

const Chevron = ({ className }: { className?: string }) => (
  <svg
    className={className}
    fill="none"
    viewBox="4 6 12 8"
    width="12"
    height="8"
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Navbar = ({ hideBorder }: { hideBorder?: boolean }) => {
  const { _ } = useLingui()
  const router = useRouter()
  const locale = (router.query.locale as string) || 'en'
  const discoverHref = (path: string) =>
    `${process.env.NEXT_PUBLIC_WEBSITE_MARKETING_URL}/${
      locale === 'it' ? 'it/' : ''
    }${path}`
  const isPartnersActive = router.asPath.startsWith('/partner')
  const [productOpen, setProductOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileProductOpen, setMobileProductOpen] = useState(false)
  const productRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        productRef.current &&
        !productRef.current.contains(e.target as Node)
      ) {
        setProductOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    const md = window.matchMedia('(min-width: 768px)')
    const handleResize = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setMobileOpen(false)
        setMobileProductOpen(false)
      }
    }
    md.addEventListener('change', handleResize)
    return () => md.removeEventListener('change', handleResize)
  }, [])

  return (
    <nav
      className={classNames('w-full bg-white', {
        'sticky top-0 z-20': !hideBorder,
        'border-b border-gray-100': !hideBorder,
      })}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        {/* Desktop + Mobile top row */}
        <div className="h-16 sm:h-20 flex items-center justify-between">
          {/* Left: logo + desktop nav links */}
          <div className="flex items-center gap-8">
            {/* Logo — placeholder, replace with wordmark SVG when available */}
            <Link href="/" className="flex items-center">
              {/* TODO: replace src with wordmark SVG */}
              <img
                src="/taylora_logo_color.svg"
                alt="Taylora"
                className="h-8 sm:h-10 w-auto"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-8">
              {/* Product dropdown */}
              <div
                ref={productRef}
                className="relative"
                onMouseEnter={() => setProductOpen(true)}
                onMouseLeave={() => setProductOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setProductOpen((v) => !v)}
                  className={classNames(
                    styles.navLink,
                    'flex items-center gap-3 h-11 text-gray-900 text-base font-semibold leading-5'
                  )}
                >
                  <Trans>Piattaforma</Trans>
                  <Chevron
                    className={classNames(
                      'text-gray-500 transform transition-transform duration-200',
                      { '-rotate-180': productOpen }
                    )}
                  />
                </button>

                {productOpen && (
                  <div
                    className={classNames(
                      'absolute top-full left-0 pt-2 z-50',
                      styles.dropdownEnter
                    )}
                  >
                    <div className="w-56 bg-gray-100 rounded-lg shadow-sm py-1.5">
                      {PRODUCT_ITEMS.map((item) => (
                        <a
                          key={item.path}
                          href={discoverHref(item.path)}
                          className="flex items-center gap-3 mx-1.5 px-2 py-2 rounded hover:bg-gray-200 transition-colors"
                        >
                          <div
                            className="w-8 h-8 flex-shrink-0 rounded-lg border flex items-center justify-center"
                            style={{
                              backgroundColor: 'rgba(0,0,0,0.02)',
                              borderColor: 'rgba(0,0,0,0.06)',
                            }}
                          >
                            <img
                              src={item.icon}
                              alt=""
                              className="w-5 h-5"
                              aria-hidden="true"
                            />
                          </div>
                          <span className="text-gray-900 text-sm font-medium leading-5 tracking-tight">
                            {_(item.label)}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                href="/partner"
                className={classNames(
                  styles.navLink,
                  { [styles.navLinkActive]: isPartnersActive },
                  'h-11 flex items-center text-gray-900 text-base font-semibold leading-5'
                )}
              >
                <Trans>Professionisti</Trans>
              </Link>

              <a
                href={discoverHref('pricing')}
                className={classNames(
                  styles.navLink,
                  'h-11 flex items-center text-gray-900 text-base font-semibold leading-5'
                )}
              >
                <Trans>Prezzi</Trans>
              </a>
            </div>
          </div>

          {/* Right: desktop CTAs */}
          <div className="hidden md:flex items-center gap-5">
            <Link
              href="/login"
              className={classNames(
                'h-11 flex items-center text-gray-900 text-base font-semibold leading-5',
                styles.navLink
              )}
            >
              <Trans>Accedi</Trans>
            </Link>
            <a
              href="https://calendly.com/taylora-com/scopri-taylora"
              target="_blank"
              rel="noopener noreferrer"
              className={classNames(
                'inline-flex items-center justify-center text-white font-semibold bg-gray-900 rounded-3xl px-5 py-2',
                styles.ctaButton
              )}
            >
              <span>
                <Trans>Richiedi una demo</Trans>&nbsp;
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
            </a>
          </div>

          {/* Mobile: compact CTA + hamburger/X */}
          <div className="md:hidden flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="relative w-11 h-11 flex flex-col justify-center items-center px-0 py-0"
              aria-label="Menu"
            >
              <span
                className="block w-5 bg-gray-400 rounded-full absolute transition-transform duration-200"
                style={{
                  height: 2,
                  transform: mobileOpen
                    ? 'rotate(45deg)'
                    : 'translateY(-5.5px)',
                }}
              />
              <span
                className="block w-5 bg-gray-400 rounded-full absolute transition-transform duration-200"
                style={{
                  height: 2,
                  transform: mobileOpen
                    ? 'rotate(-45deg)'
                    : 'translateY(5.5px)',
                }}
              />
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        <div
          className={classNames('md:hidden', styles.accordion, {
            [styles.accordionOpen]: mobileOpen,
          })}
        >
          <div className={styles.accordionInner}>
            <div className="flex flex-col py-3">
              {/* Product accordion */}
              <button
                type="button"
                onClick={() => setMobileProductOpen((v) => !v)}
                className="flex items-center gap-2 w-full px-4 py-3 text-gray-900 text-base font-semibold text-left"
              >
                <Trans>Prodotto</Trans>
                <Chevron
                  className={classNames(
                    'text-gray-500 transform transition-transform duration-200',
                    { '-rotate-180': mobileProductOpen }
                  )}
                />
              </button>

              {/* Product sub-accordion */}
              <div
                className={classNames(styles.accordion, {
                  [styles.accordionOpen]: mobileProductOpen,
                })}
              >
                <div className={styles.accordionInner}>
                  <div className="px-4 pb-2 flex flex-col gap-0.5">
                    {PRODUCT_ITEMS.map((item) => (
                      <a
                        key={item.path}
                        href={discoverHref(item.path)}
                        className="flex items-center gap-3 px-3 py-2 rounded hover:bg-gray-100 transition-colors"
                      >
                        <div
                          className="w-7 h-7 flex-shrink-0 rounded-lg border flex items-center justify-center"
                          style={{
                            backgroundColor: 'rgba(0,0,0,0.02)',
                            borderColor: 'rgba(0,0,0,0.06)',
                          }}
                        >
                          <img
                            src={item.icon}
                            alt=""
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                          />
                        </div>
                        <span className="text-gray-900 text-sm font-medium leading-5 tracking-tight">
                          {_(item.label)}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <Link
                href="/partner"
                className={classNames(
                  'px-4 py-3 text-gray-900 text-base font-semibold'
                )}
              >
                <Trans>Professionisti</Trans>
              </Link>

              <a
                href={discoverHref('pricing')}
                className="px-4 py-3 text-gray-900 text-base font-semibold"
              >
                <Trans>Prezzi</Trans>
              </a>

              {/* Bottom CTAs */}
              <div className="px-4 pt-3 flex flex-col items-center gap-4">
                <div className="w-full border-t border-gray-100" />
                <Link
                  href="/login"
                  className="py-2 text-gray-900 text-base font-semibold"
                >
                  <Trans>Accedi</Trans>
                </Link>
                <a
                  href="https://calendly.com/taylora-com/scopri-taylora"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={classNames(
                    'w-full inline-flex items-center justify-center text-white font-semibold bg-gray-900 rounded-3xl px-5 py-2',
                    styles.ctaButton
                  )}
                >
                  <span>
                    <Trans>Richiedi una demo</Trans>&nbsp;
                    <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Navbar
